export type OverwatchKeyType =
  | 'single'
  | 'series'
  | 'series.xy'
  | 'collection'
  | 'text'
  | 'markdown'
  | 'enum'
  | 'date'
  | 'datetime'
  | 'link';

export type OverwatchUnit =
  | 'percent'
  | 'percent_change'
  | 'percentage_points'
  | 'ratio'
  | 'currency'
  | 'count'
  | 'duration_seconds'
  | 'bytes';

export type OverwatchGranularity = 'hour' | 'day' | 'week' | 'month' | 'quarter';

export interface IOverwatchFieldDefinition {
  type: Exclude<OverwatchKeyType, 'collection' | 'series' | 'series.xy'>;
  label: string;
  required: boolean;
  unit?: OverwatchUnit;
}

export interface IOverwatchKeyDefinition {
  type: OverwatchKeyType;
  label: string;
  unit?: OverwatchUnit;
  fields?: Record<string, IOverwatchFieldDefinition>;
}

/** One entry of `GET /v4/dash/overwatch/widgets`, which answers a bare array. */
export interface IOverwatchWidgetDefinition {
  key: string;
  section: string;
  keys: Record<string, IOverwatchKeyDefinition>;
}

export type OverwatchKeyState =
  | 'available'
  | 'valid_zero'
  | 'stale'
  | 'not_available'
  | 'not_configured'
  | 'insufficient_data'
  | 'unavailable'
  | 'upstream_error'
  | 'entitlement_unavailable';

/** States whose `data` carries a value; every other state sends `data: null`. */
export const OVERWATCH_VALUE_STATES: ReadonlySet<OverwatchKeyState> = new Set<OverwatchKeyState>([
  'available',
  'valid_zero',
  'stale'
]);

export interface IOverwatchEnum {
  code: string;
  label: string;
}

export interface IOverwatchLink {
  url: string;
  label: string;
}

export interface IOverwatchPoint {
  x: string;
  y: number | null;
}

export interface IOverwatchRecord {
  id: string | number;
  [field: string]: unknown;
}

export interface IOverwatchEnvelope<T = unknown> {
  state: OverwatchKeyState;
  data: T | null;
  reason?: string;
  as_of?: string;
  granularity?: OverwatchGranularity;
  currency?: string;
  total_count?: number | null;
  next_cursor?: string | null;
}

export type OverwatchRequestStatus = 'pending' | 'processing' | 'completed' | 'failed';

export type OverwatchPollErrorCode =
  | 'not_found'
  | 'permission_denied'
  | 'widget_unavailable'
  | 'invalid_cursor'
  | 'invalid_widget_response'
  | 'timeout'
  | 'retries_exhausted'
  | 'internal_error';

export interface IOverwatchRequestError {
  code: OverwatchPollErrorCode;
  message: string;
}

/** One entry of the POST (202) and poll responses. */
export interface IOverwatchWidgetRequest {
  id: number;
  key: string | null;
  status: OverwatchRequestStatus;
  data: Record<string, IOverwatchEnvelope> | null;
  error: IOverwatchRequestError | null;
}

export interface IOverwatchWidgetRequestsResponse {
  data: IOverwatchWidgetRequest[];
  meta: Record<string, unknown>;
}

export interface IOverwatchCreateRequest {
  keys: string[];
  params?: Record<string, { cursor: string }>;
  refresh?: boolean;
}

export type OverwatchActionKey =
  | 'create_support_ticket'
  | 'schedule_renewal_review'
  | 'book_a_demo';

export type OverwatchActionOriginType = 'anomaly' | 'incident';

export interface IOverwatchActionOption {
  code: string;
  label: string;
  service?: string;
}

export interface IOverwatchActionInput {
  type: 'enum' | 'text' | 'origin';
  required: boolean;
  max_length?: number;
  multiline?: boolean;
  options?: IOverwatchActionOption[];
  /** The origin input only: the origin types the caller may use. */
  types?: OverwatchActionOriginType[];
  /** The widget key whose rows the value must come from. */
  source?: string;
}

/**
 * One entry of `GET /v4/dash/overwatch/actions`: an action is listed only when the caller may submit
 * it, and its `origin` input only when the caller may use at least one origin type.
 */
export interface IOverwatchActionDefinition {
  key: string;
  label?: string;
  hidden?: boolean;
  inputs?: Record<string, IOverwatchActionInput>;
}

export interface IOverwatchActionOrigin {
  type: OverwatchActionOriginType;
  reference: string;
}

export interface IOverwatchActionSubmission {
  client_request_id: string;
  input: Record<string, unknown>;
}

export interface IOverwatchActionTicket {
  ticket_id: string;
  title?: string;
  status?: IOverwatchEnum | null;
  priority?: IOverwatchEnum | null;
  preferred_severity?: IOverwatchEnum | null;
}

/** The 201 body's `data`. `ticket` is null for an action whose ticket never lists in SS-05. */
export interface IOverwatchActionResult {
  action: string;
  client_request_id: string;
  submitted_at: string;
  ticket: IOverwatchActionTicket | null;
}
